import { HttpClient } from '@angular/common/http';
import { EventEmitter, Injectable, signal } from '@angular/core';
import { lastValueFrom, Subject } from 'rxjs';
import { SelectOption, User } from 'src/app/shared';
import { Http } from 'src/app/shared/services/http.service';
import { Delegatee } from '../components/delegate/act-delegate-page.component';
import { HelpItem } from '../models/HelpItem';
import { LogoResults } from '../models/LogoResults';
import { WelcomeMessage } from '../models/WelcomeMessageText';
import { UserLogout } from '../models/user-logout';
import { UserActionService } from '../../shared';
import { Router } from '@angular/router';
import { clients } from 'src/app/shared/models/user';
import { thresholdSturges } from 'd3';
import { EncryptionService } from './encryption.service';

@Injectable({
  providedIn: 'root',
})
export class Session {
  get user() {
    return this._user;
  }

  get isAuditorPreview(): boolean {
    return !!(this._user?.isEmulating || this._user?.emulatorContext?.isEmulating);
  }

  get hideAnnouncements(): boolean {
    return sessionStorage.getItem('hide-announcements') === 'true';
  }
  set hideAnnouncements(value: boolean) {
    sessionStorage.setItem('hide-announcements', `${value}`);
  }

  private _user: User;
  private userLogout: UserLogout;
  private auditorRedirectLocation: string;
  supervisorEmpEmpUNum: string;
  currentAuditLogId: number = -1;
  public currentDelegatee: Delegatee | null;

  isEmulating$ = new EventEmitter<boolean>();
  isDelegating$ = new EventEmitter<boolean>();
  updateActivity = new EventEmitter<boolean>();
  isDelegating = signal<boolean>(false);
  currDelegatee = signal<Delegatee | null>(null);

  private selectedMultiClientSource = new Subject<string>();
  selectClient$ = this.selectedMultiClientSource.asObservable();

  constructor(
    private http: HttpClient,
    private http1: Http,
    private userAction: UserActionService,
    private encrypt: EncryptionService
  ) {
    this.supervisorEmpEmpUNum = sessionStorage.getItem('empEmUNum');
    this.currentDelegatee = this.delegatee;
  }

  async setLanguagePreferenceForUser(languageCode: string): Promise<boolean> {
    try {
      await this.http1.get<string>('/api/user/antiforgery');
      const result = await this.http1.post<boolean>(
        '/api/user/SetLanguagePreference',
        { languageCode: languageCode }
      );
      
      if (result) {
        // Update the user object with the new language code
        if (this._user) {
          this._user.defaultLanguageCode = languageCode;
        }
      }
      
      return result;
    } catch (error) {
      console.error('Error setting language preference:', error);
      return false;
    }
  }
  async loadUser() {
    const options = { cache: true };
    if (!this._user) {
      if (Number(this.SelectedContNum) > -1) {
        this._user = await this.http1.get<User>(
          '/api/user/loginwithclient/' + this.SelectedContNum
        );
      } else {
        this._user = await this.http1.get<User>('/api/user');
        this._user.ssn = this.encrypt.authDecrypt(
          this._user.ssn,
          this._user.sessionId
        );
      }

      if (this._user == null) {
        window.location.href = 'https://login.mysedgwick.com/google';
      } else {
        await this.http1.get<string>('/api/user/antiforgery');

        this.isDelegating.set(this._user.isDelegating);
        this.currDelegatee.set(this.delegatee);
        if (this._user.timezone == undefined) {
          this._user.timezone =
            new Intl.DateTimeFormat().resolvedOptions().timeZone;
        }
      }
    }    
  }

  async ChangeClient(value: string) {
    this.selectedMultiClientSource.next(value);
  }

  async reloadAuditorDashboard() {
    /* this.router.navigateByUrl('/history', {skipLocationChange: true}).then(() => {
      this.router.navigate(['/auditor']);
    });*/
    window.location.reload();
  }

  async SetSelectedClient(ContNum: string, ClientName: string) {
    sessionStorage.setItem('SelectedContNum', ContNum);
    sessionStorage.setItem('SelectedClientName', ClientName);
    await sessionStorage.removeItem('UpdatedSelectedContNum');
    await sessionStorage.removeItem('UpdatedSelectedClientName');
  }
  async SetUpdatedClient(ContNum: string, ClientName?: string) {
    sessionStorage.setItem('UpdatedSelectedContNum', ContNum);
    if (ClientName) {
      sessionStorage.setItem('UpdatedSelectedClientName', ClientName);
    }
  }
  async RemoveSelectedClient() {
    if (
      sessionStorage.getItem('SelectedContNum') != null ||
      sessionStorage.getItem('SelectedContNum') != undefined
    ) {
      await sessionStorage.removeItem('SelectedContNum');
      await sessionStorage.removeItem('SelectedClientName');
      await sessionStorage.removeItem('UpdatedSelectedContNum');
      await sessionStorage.removeItem('UpdatedSelectedClientName');
    }
  }
  public get SelectedContNum(): string {
    if (
      sessionStorage.getItem('SelectedContNum') != null ||
      sessionStorage.getItem('SelectedContNum') != undefined
    ) {
      return sessionStorage.getItem('SelectedContNum');
    } else {
      return '-1';
    }
  }
  public get SelectedUpdatedContNum(): string {
    if (
      sessionStorage.getItem('UpdatedSelectedContNum') != null ||
      sessionStorage.getItem('UpdatedSelectedContNum') != undefined
    ) {
      return sessionStorage.getItem('UpdatedSelectedContNum');
    } else {
      return '-1';
    }
  }
  public get SelectedUpdatedClientName(): string {
    if (
      sessionStorage.getItem('UpdatedSelectedClientName') != null ||
      sessionStorage.getItem('UpdatedSelectedClientName') != undefined
    ) {
      return sessionStorage.getItem('UpdatedSelectedClientName');
    } else {
      return '-1';
    }
  }
  public get SelectedClientName(): string {
    if (
      sessionStorage.getItem('SelectedClientName') != null ||
      sessionStorage.getItem('SelectedClientName') != undefined
    ) {
      return sessionStorage.getItem('SelectedClientName');
    } else {
      return '-1';
    }
  }

  /**
   * Company label for the header. Auditors follow the client they picked.
   * Everyone else still uses the client on the signed-in user.
   */
  public get clientHeader(): string {
    const contNum = this.SelectedContNum;
    const storedName = this.SelectedClientName;
    if (Number(contNum) > -1 && storedName && storedName !== '-1') {
      return `${storedName} - ${contNum} (${contNum})`;
    }

    const name = this.user?.clientName;
    const userContNum = this.user?.contNum;
    if (!name) {
      return '';
    }
    return userContNum ? name + ' (' + userContNum + ')' : name;
  }

  async emulateUser(
    empId: string,
    claimId: string,
    contNum: string,
    empUnum: string,
    hrMasterUid: string,
    lob?: string,
    isController?: boolean,
    userName?: string
  ) {
    this._user = null;
    await this.http1.clearCache();

    this._user = await lastValueFrom(
      this.http.post<User>('/api/emulation/EmulateUser', {
        EmpId: empId,
        ClaimId: claimId,
        ContNum: contNum,
        EmpUnum: empUnum,
        HrMasterUid: hrMasterUid,
        Lob: lob ?? null,
        IsController: isController ?? false,
        UserName: userName,
      })
    );
    await this.http1.get<string>('/api/user/antiforgery');

    if (this._user.timezone == undefined) {
      this._user.timezone =
        new Intl.DateTimeFormat().resolvedOptions().timeZone;
    }
    this.isDelegating.set(false);
    this.currDelegatee.set(null);
    sessionStorage.removeItem('currentDelegatee');
    this.isEmulating$.next(true);
  }

  async emulateLogStart(claimId: string) {
    this.currentAuditLogId = -1;
    const emulateRequest: any = {
      AuditorLogId: 0,
      AuditorUserId: '',
      EmulateeClaimUid: claimId,
      EmulateeContNum: 0,
    };
    await this.http1.get<string>('/api/user/antiforgery');
    await this.http
      .post<any>('/api/emulation/EmulationStartLog', emulateRequest)
      .subscribe((result) => {
        this.currentAuditLogId = parseInt(result);
      });
  }

  async emulateLogEnd() {
    const emulateRequest: any = {
      AuditorLogId: this.currentAuditLogId,
      AuditorUserId: '',
      EmulateeEmpId: 0,
      EmulateeClaimUid: '',
      EmulateeContNum: 0,
    };
    await this.http
      .post<any>('/api/emulation/EmulationEndLog', emulateRequest)
      .subscribe((result) => {
        this.currentAuditLogId = -1;
      });
  }

  async stopEmulation(redirectUrl: string) {
    this._user = null;
    const options = { cache: true };
    sessionStorage.setItem('closed-announcements', 'false');
    await this.http1.get<string>('/api/user/antiforgery');
    await this.emulateLogEnd();
    this._user = await this.http
      .post<User>('/api/emulation/StopEmulation', {})
      .toPromise();

    if (this._user.timezone == undefined) {
      this._user.timezone =
        new Intl.DateTimeFormat().resolvedOptions().timeZone;
    }

    this.isEmulating$.next(false);
    window.location.href = redirectUrl ? redirectUrl : '/';
  }

  async startDelegation(delegatee: Delegatee): Promise<boolean | string> {
    if (this.isAuditorPreview) {
      return false;
    }

    try {
      await this.http1.clearCache();
      await this.userAction.setBustCache(true);
      const emulationUser = {
        EmpId: delegatee.delegatorEmpId,
        EncryptedEmpId: delegatee.encryptedDelegatorEmpId,
        HrMasterUid: delegatee.delegatorHrMasterUid,
        ContNum: delegatee.contNum.toString(),
        EmpUnum: delegatee.delegatorEmpUnum,
        CanReportClaim: delegatee.reportClaim,
        CanAddAbsence: delegatee.addAbsences,
        CanGetEmailCorrespondence: delegatee.emailCorrespondence,
      };
      const body: any = {
        emulateUser: emulationUser,
        delegator: delegatee,
      };
      await this.http1.get<string>('/api/user/antiforgery');
      const response = await this.http
        .post<User>('/api/delegation/start-delegation', body)
        .toPromise();

      if (response.error != null) {
        return 'Error';
      } else {
        this.isDelegating.set(true);
        this.isDelegating$.next(true);
        sessionStorage.setItem('currentDelegatee', JSON.stringify(delegatee));
        this.currDelegatee.set(delegatee);
        this._user = response;
        await this.startDelegationLog(delegatee);

        if (this._user.timezone == undefined) {
          this._user.timezone =
            new Intl.DateTimeFormat().resolvedOptions().timeZone;
        }
        await this.userAction.setBustCache(true);
        this.isDelegating.set(true);
        return true;
      }
    } catch (error) {
      return false;
    }
  }

  async endDelegation(): Promise<boolean> {
    await this.http1.clearCache();
    await this.userAction.setBustCache(true);
    this.isDelegating.set(false);
    this._user = null;

    await this.http1.get<string>('/api/user/antiforgery');
    this._user = await this.http
      .post<User>('/api/delegation/stop-delegation', {})
      .toPromise();
    await this.stopDelegationLog(
      this.currDelegatee ? this.currDelegatee() : null
    );

    await sessionStorage.removeItem('currentDelegatee');
    await this.currDelegatee.set(null);
    if (this._user.timezone == undefined) {
      this._user.timezone =
        new Intl.DateTimeFormat().resolvedOptions().timeZone;
    }
    await this.isDelegating$.next(false);
    return true;
  }

  async logoff() {
    if (this.user.delegateeContext != null) {
      await this.endDelegation();
    }
    sessionStorage.clear();
    await this.http.get<string>('/api/user/antiforgery');

    const logoutRequestData = {
      userType: this._user.userType || '',
      userLogoutUrl: this._user.logOutUrl || '',
      languageCode: this._user.defaultLanguageCode || 'en',
    };
    const logoutRequest = new userLogoutRequest(logoutRequestData);
    this.userLogout = await this.http1.post<UserLogout>(
      `/api/auth/sign-out`,
      logoutRequest
    );
    this.RemoveSelectedClient();
    window.location.href = this.userLogout.logoutURL;
  }

  GetLogoDetails() {
    return this.http1.get<LogoResults>(`/api/user/GetLogoDetails`);
  }

  async GetWelcomeMessage(): Promise<WelcomeMessage[]> {
    const actionString = sessionStorage.getItem('mgrAction');
    const action = JSON.parse(actionString);
    let role = this.user.role;
    if (
      (this.user.role == 'SUPERVISOR' ||
        this.user.role == 'OMNI' ||
        this.user.role == 'OVERSEER') &&
      action == 'ViewOwnEmpDb'
    ) {
      role = 'HRM';
    }
    return await this.http1.get<WelcomeMessage[]>(
      `/api/claimant/${role}/GetWelcomeMessage`
    );
  }

  async GetAuditorRedirectLocation() {
    const temp = this.auditorRedirectLocation;
    this.auditorRedirectLocation = null;
    return temp;
  }

  async SetAuditorRedirectLocation(value: string) {
    this.auditorRedirectLocation = value;
  }

  async getInternalAuditorClientsList(): Promise<clients[]> {
    return await this.http1.get<clients[]>(
      `/api/user/GetInternalAuditorClientsList`
    );
  }

  async startDelegationLog(
    delegatee: Delegatee
  ): Promise<boolean | { Message: string }> {
    try {
      await this.http1.get<string>('/api/user/antiforgery');
      await this.http
        .post<any>('/api/delegation/start-delegation-log', delegatee)
        //.toPromise();
        .subscribe((result) => {
          return result;
        });
      return true;
    } catch (error) {
      console.error(error);
      return false;
    }
  }

  async stopDelegationLog(delegatee: Delegatee | null): Promise<boolean> {
    try {
      await this.http1.get<string>('/api/user/antiforgery');
      await this.http
        .post<boolean>('/api/delegation/stop-delegation-log', delegatee)
        .toPromise();
      return true;
    } catch (error) {
      console.error(error);
      return false;
    }
  }

  get delegatee(): Delegatee | null {
    if (!this.currentDelegatee) {
      const storedDelegatee = sessionStorage.getItem('currentDelegatee');
      if (storedDelegatee) {
        this.currentDelegatee = JSON.parse(storedDelegatee);
      }
    }
    return this.currentDelegatee;
  }
}

export class emulateUserRequest {
  empId: string;
  claimId: string;

  constructor(values: emulateUserRequest) {
    Object.assign(this, values);
  }
}

export class userLogoutRequest {
  userType: string;
  userLogoutUrl: string;
  languageCode: string;
  constructor(values: userLogoutRequest) {
    Object.assign(this, values);
  }
}
